import type { ReactNode } from 'react';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

/** Consistent single-screen onboarding step: eyebrow + title + lead, the step's
 * action, an optional "how we keep you safe" note, and a footer of controls. */
export function StepScaffold({
  eyebrow,
  title,
  lead,
  safety,
  children,
  footer,
  onBack,
}: {
  eyebrow: string;
  title: string;
  lead: string;
  safety?: string;
  children?: ReactNode;
  footer: ReactNode;
  /** When set, a "Back" control fills the footer's left slot so the user can
   * revisit the previous step. Omitted on the first step. */
  onBack?: () => void;
}) {
  return (
    <Card className="space-y-6 p-8">
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-widest text-iris">{eyebrow}</p>
        <h1 className="font-display text-2xl font-bold text-bone">{title}</h1>
        <p className="max-w-prose text-sm leading-relaxed text-muted">{lead}</p>
      </div>

      {children}

      {safety && (
        <p className="flex items-start gap-2 rounded-sm border border-hairline bg-surface-2 px-3 py-2 text-xs text-haze">
          <ShieldCheck className="mt-0.5 h-3.5 w-3.5 flex-none text-iris" aria-hidden />
          <span>{safety}</span>
        </p>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {onBack ? (
          <Button type="button" variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Back
          </Button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">{footer}</div>
      </div>
    </Card>
  );
}
